import React, { useState, useEffect } from "react";
import { Search, Plus, Edit2, Trash2, Eye, Star, StarOff, ArrowUpDown, Upload, X, Package, AlertCircle } from 'lucide-react';
import { inventoryService } from "../../../../Services/combineInventoryService";
import StatusMaterialList from "../../../../components/materialLIst/StatusMaterialList";
import { useSelector } from "react-redux";
import DeleteConfirmationModal from "./DeleteConfirmationModal ";
import DeleteItemConfirmationModal from "./DeleteItemConfirmationModal ";

// Utility Functions
const validateInventory = (inventory) => {
  const errors = {};
  if (!inventory.inventoryName.trim()) {
    errors.inventoryName = 'Please fill the Inventory Name';
  }
  if (!inventory.description.trim()) {
    errors.description = 'Please fill the Inventory Description';
  }
  return errors;
};

const validateItem = (item) => {
  const errors = {};
  if (!item.materialName.trim()) {
    errors.materialName = 'Please fill the Material Name';
  }
  if (!item.materialNo.toString().trim()) {
    errors.materialNo = 'Please fill the Material Number';
  }
  if (!item.quantityAvailable || parseInt(item.quantityAvailable) <= 0) {
    errors.quantityAvailable = 'Please enter a valid quantity';
  }
  if (!item.weight || parseFloat(item.weight) <= 0) {
    errors.weight = 'Please enter a valid weight';
  }
  return errors;
};

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'error' ? 'bg-red-500' : type === 'success' ? 'bg-green-500' : 'bg-blue-500';

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2`}>
      {type === 'error' && <AlertCircle size={20} />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <X size={16} />
      </button>
    </div>
  );
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden`}>
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

// Create/Edit Inventory Form
const InventoryForm = ({
  inventory,
  onChange,
  errors,
  onSubmit,
  onCancel,
  isLoading = false,
  isEdit = false
}) => {
  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inventory Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Your inventory's name"
            value={inventory.inventoryName}
            onChange={(e) => onChange({ ...inventory, inventoryName: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.inventoryName ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {errors.inventoryName && (
            <p className="text-red-500 text-sm mt-1">{errors.inventoryName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inventory Description <span className="text-red-500">*</span>
          </label>
          <textarea
            placeholder="Your inventory's description"
            value={inventory.description}
            onChange={(e) => onChange({ ...inventory, description: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
            rows="3"
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 p-6 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Inventory' : 'Create Inventory')}
        </button>
      </div>
    </div>
  );
};

// Add Item Form Component
const AddItemForm = ({
  item,
  onChange,
  errors,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inventory List Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Inventory List Name"
              value={item.inventoryListName}
              onChange={(e) => onChange({ ...item, inventoryListName: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.inventoryListName ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.inventoryListName && (
              <p className="text-red-500 text-sm mt-1">{errors.inventoryListName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Material Number <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Material Number"
              value={item.materialNo}
              onChange={(e) => onChange({ ...item, materialNo: parseInt(e.target.value) || '' })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.materialNo ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.materialNo && (
              <p className="text-red-500 text-sm mt-1">{errors.materialNo}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Material Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Material Name"
              value={item.materialName}
              onChange={(e) => onChange({ ...item, materialName: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.materialName ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.materialName && (
              <p className="text-red-500 text-sm mt-1">{errors.materialName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Material Description"
              value={item.description}
              onChange={(e) => onChange({ ...item, description: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weight (kg) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.1"
              placeholder="0.1"
              value={item.weight}
              onChange={(e) => onChange({ ...item, weight: parseFloat(e.target.value) || '' })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.weight ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.weight && (
              <p className="text-red-500 text-sm mt-1">{errors.weight}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Available Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              placeholder="Enter quantity"
              value={item.quantityAvailable}
              onChange={(e) => onChange({ ...item, quantityAvailable: parseInt(e.target.value) || '' })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.quantityAvailable ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.quantityAvailable && (
              <p className="text-red-500 text-sm mt-1">{errors.quantityAvailable}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 p-6 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Adding...' : 'Add Item'}
        </button>
      </div>
    </div>
  );
};

// View Inventory Modal Content
const ViewInventoryContent = ({ inventory, inventoryItems, onAddItem, onDeleteItem, isLoadingItems }) => {
  return (
    <div className="p-6">
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{inventory.inventoryName}</h3>
            <p className="text-gray-600 mb-3">{inventory.description || 'No description provided'}</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Total Items:</span>
                <span className="ml-2 font-medium">{inventory.itemCount || 0}</span>
              </div>
              <div>
                <span className="text-gray-500">Total Weight:</span>
                <span className="ml-2 font-medium">{inventory.totalWeight || 0} kg</span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className="ml-2 font-medium text-green-600">{inventory.status}</span>
              </div>
              <div>
                <span className="text-gray-500">Created:</span>
                <span className="ml-2 font-medium">{new Date(inventory.createdAt).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-gray-500">Favorite:</span>
                <span className="ml-2">{inventory.isFavorite ? '⭐ Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white border rounded-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-medium text-gray-900">Inventory Items</h4>
          <button
            onClick={onAddItem}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Item
          </button>
        </div>
        <div className="p-4">
          {isLoadingItems ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading items...</p>
            </div>
          ) : inventoryItems?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Material No.</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Material Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">List Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Weight (kg)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Available Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Total Weight</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inventoryItems.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.materialNo}</td>
                      <td className="px-4 py-3 text-sm font-medium">{item.materialName}</td>
                      <td className="px-4 py-3 text-sm">{item.inventoryListName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                      <td className="px-4 py-3 text-sm">{item.weight}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-medium ${item.quantityAvailable < 10 ? 'text-red-600' : 'text-green-600'}`}>
                          {item.quantityAvailable}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{item.totalWeight}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onDeleteItem(item._id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-400" />
              <p>No items in this inventory yet</p>
              <p className="text-sm mt-1">Add items to get started</p>
              <button
                onClick={onAddItem}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Add First Item
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const InventoryOverview = () => {
  const userId = useSelector(state => state?.auth?.loggedInUser?._id)
  // State management
  const [inventories, setInventories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('inventoryName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    inventory: null,
    isLoading: false
  });

  const handleDeleteInventory = (inventory) => {
    setDeleteModal({
      isOpen: true,
      inventory: inventory,
      isLoading: false
    });
  };

  const handleDeleteItem = (item) => {
    setDeleteItemModal({
      isOpen: true,
      item: item,
      isLoading: false
    });
  };

  const [deleteItemModal, setDeleteItemModal] = useState({
    isOpen: false,
    item: null,
    isLoading: false
  });

  const confirmDeleteInventory = async () => {
    if (!deleteModal.inventory) return;

    setDeleteModal(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await inventoryService.mainInventory.delete(deleteModal.inventory._id);

      if (result.success) {
        showToast('Inventory deleted successfully', 'success');
        loadInventories();
        setDeleteModal({ isOpen: false, inventory: null, isLoading: false });
      } else {
        showToast(result.message || 'Failed to delete inventory', 'error');
        setDeleteModal(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      showToast('Failed to delete inventory', 'error');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const closeDeleteModal = () => {
    if (!deleteModal.isLoading) {
      setDeleteModal({ isOpen: false, inventory: null, isLoading: false });
    }
  };

  // Modal states
  const [modals, setModals] = useState({
    create: false,
    view: false,
    edit: false,
    addItem: false
  });

  const [currentInventory, setCurrentInventory] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Form states
  const [newInventory, setNewInventory] = useState({
    inventoryName: '',
    description: '',
    createdBy: userId
  });

  const [editInventory, setEditInventory] = useState({
    inventoryName: '',
    description: '',
    createdBy: userId
  });

  const [newItem, setNewItem] = useState({
    inventoryListName: '',
    materialNo: '',
    materialName: '',
    description: '',
    weight: '',
    quantityAvailable: ''
  });

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Load inventories from API
  const loadInventories = async (params = {}) => {
    setIsLoading(true);
    try {
      const result = await inventoryService.mainInventory.getAll({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        sortBy,
        sortOrder,
        ...params
      });

      if (result.success) {
        setInventories(result.data);
        setTotalPages(result.pagination?.pages || 1);
      } else {
        showToast(result.message || 'Failed to load inventories', 'error');
      }
    } catch (error) {
      showToast('Network error occurred', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Load inventory items
  const loadInventoryItems = async (inventoryId) => {
    setIsLoadingItems(true);
    try {
      const result = await inventoryService.items.getByMainInventoryId(inventoryId);

      if (result.success) {
        setInventoryItems(result.data);
      } else {
        showToast(result.message || 'Failed to load inventory items', 'error');
      }
    } catch (error) {
      showToast('Failed to load inventory items', 'error');
    } finally {
      setIsLoadingItems(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadInventories();
  }, [currentPage, searchTerm, sortBy, sortOrder]);

  // Modal management
  const openModal = (modalName, inventory = null) => {
    setCurrentInventory(inventory);
    setModals(prev => ({ ...prev, [modalName]: true }));
    setFormErrors({});

    if (modalName === 'view' && inventory) {
      loadInventoryItems(inventory._id);
    }
  };

  const closeModal = (modalName) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
    setFormErrors({});
    setCurrentInventory(null);
    setInventoryItems([]);

    if (modalName === 'create') {
      setNewInventory({ inventoryName: '', description: '' });
    }
    if (modalName === 'addItem') {
      setNewItem({
        inventoryListName: '',
        materialNo: '',
        materialName: '',
        description: '',
        weight: '',
        quantityAvailable: ''
      });
    }
  };

  // Inventory operations
  const handleCreateInventory = async () => {
    const errors = validateInventory(newInventory);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const result = await inventoryService.mainInventory.create(newInventory);

      if (result.success) {
        showToast('Inventory created successfully', 'success');
        loadInventories();
        closeModal('create');
      } else {
        showToast(result.message || 'Failed to create inventory', 'error');
      }
    } catch (error) {
      showToast('Failed to create inventory', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditInventory = async () => {
    const errors = validateInventory(editInventory);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const result = await inventoryService.mainInventory.update(currentInventory._id, editInventory);

      if (result.success) {
        showToast('Inventory updated successfully', 'success');
        loadInventories();
        closeModal('edit');
      } else {
        showToast(result.message || 'Failed to update inventory', 'error');
      }
    } catch (error) {
      showToast('Failed to update inventory', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // const handleDeleteInventory = async (inventory) => {
  //   if (window.confirm(`Are you sure you want to delete "${inventory.inventoryName}"?`)) {
  //     try {
  //       const result = await inventoryService.mainInventory.delete(inventory._id);

  //       if (result.success) {
  //         showToast('Inventory deleted successfully', 'success');
  //         loadInventories();
  //       } else {
  //         showToast(result.message || 'Failed to delete inventory', 'error');
  //       }
  //     } catch (error) {
  //       showToast('Failed to delete inventory', 'error');
  //     }
  //   }
  // };

  const handleEditSetup = (inventory) => {
    setEditInventory({
      inventoryName: inventory.inventoryName,
      description: inventory.description
    });
    openModal('edit', inventory);
  };

  const handleAddItem = async () => {
    const errors = validateItem(newItem);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const itemData = {
        ...newItem,
        mainInventoryId: currentInventory._id
      };

      const result = await inventoryService.items.create(itemData);

      if (result.success) {
        showToast('Item added successfully', 'success');
        loadInventoryItems(currentInventory._id);
        loadInventories(); // Refresh to update counts
        closeModal('addItem');
      } else {
        showToast(result.message || 'Failed to add item', 'error');
      }
    } catch (error) {
      showToast('Failed to add item', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // const handleDeleteItem = async (itemId) => {
  //   if (window.confirm('Are you sure you want to delete this item?')) {
  //     try {
  //       const result = await inventoryService.items.delete(itemId);

  //       if (result.success) {
  //         showToast('Item deleted successfully', 'success');
  //         loadInventoryItems(currentInventory._id);
  //         loadInventories(); // Refresh to update counts
  //       } else {
  //         showToast(result.message || 'Failed to delete item', 'error');
  //       }
  //     } catch (error) {
  //       showToast('Failed to delete item', 'error');
  //     }
  //   }
  // };

  const toggleFavorite = async (inventory) => {
    try {
      const result = await inventoryService.mainInventory.toggleFavorite(inventory._id);

      if (result.success) {
        showToast(`Inventory ${result.data.isFavorite ? 'added to' : 'removed from'} favorites`, 'success');
        loadInventories();
      } else {
        showToast(result.message || 'Failed to update favorite status', 'error');
      }
    } catch (error) {
      showToast('Failed to update favorite status', 'error');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const confirmDeleteItem = async () => {
    if (!deleteItemModal.item) return;

    setDeleteItemModal(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await inventoryService.items.delete(deleteItemModal.item);

      if (result.success) {
        showToast('Item deleted successfully', 'success');
        loadInventoryItems(currentInventory._id);
        loadInventories(); // Refresh to update counts
        setDeleteItemModal({ isOpen: false, item: null, isLoading: false });
      } else {
        showToast(result.message || 'Failed to delete item', 'error');
        setDeleteItemModal(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      showToast('Failed to delete item', 'error');
      setDeleteItemModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Inventory Overview</h1>
        <p className="text-gray-600">Manage your project inventories</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search inventories..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openModal('create')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Inventory
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading inventories...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('inventoryName')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Inventory Name
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('itemCount')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Items Count
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('totalWeight')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Total Weight
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventories.map((inventory) => (
                    <tr key={inventory._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleFavorite(inventory)}
                            className="text-gray-400 hover:text-yellow-500"
                          >
                            {inventory.isFavorite ? (
                              <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            ) : (
                              <StarOff className="h-4 w-4" />
                            )}
                          </button>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{inventory.inventoryName}</div>
                            {inventory.isFavorite && (
                              <div className="text-xs text-yellow-600">★ Favorite</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {inventory.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {inventory.itemCount || 0} items
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-medium">
                          {inventory.totalWeight || 0} kg
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${inventory.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : inventory.status === 'inactive'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {inventory.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(inventory.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openModal('view', inventory)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Inventory"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditSetup(inventory)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit Inventory"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openModal('addItem', inventory)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Add Items"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInventory(inventory)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Inventory"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && inventories.length === 0 && (
          <div className="text-center py-12">
            {searchTerm ? (
              <>
                <Search size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No inventories found</h3>
                <p className="text-gray-500">Try searching with different keywords</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-blue-600 hover:text-blue-800"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <Plus size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Inventories Found</h3>
                <p className="text-gray-500 mb-6">Create your first inventory to get started</p>
                <button
                  onClick={() => openModal('create')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                  Create Inventory
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={modals.create}
        onClose={() => closeModal('create')}
        title="Create New Inventory"
      >
        <InventoryForm
          inventory={newInventory}
          onChange={setNewInventory}
          errors={formErrors}
          onSubmit={handleCreateInventory}
          onCancel={() => closeModal('create')}
          isLoading={isLoading}
        />
      </Modal>

      <Modal
        isOpen={modals.edit}
        onClose={() => closeModal('edit')}
        title="Edit Inventory"
      >
        <InventoryForm
          inventory={editInventory}
          onChange={setEditInventory}
          errors={formErrors}
          onSubmit={handleEditInventory}
          onCancel={() => closeModal('edit')}
          isLoading={isLoading}
          isEdit={true}
        />
      </Modal>

      <Modal
        isOpen={modals.view}
        onClose={() => closeModal('view')}
        title={`View Inventory: ${currentInventory?.inventoryName || ''}`}
        size="lg"
      >
        {currentInventory && (
          <ViewInventoryContent
            inventory={currentInventory}
            inventoryItems={inventoryItems}
            onAddItem={() => {
              closeModal('view');
              openModal('addItem', currentInventory);
            }}
            onDeleteItem={handleDeleteItem}
            isLoadingItems={isLoadingItems}
          />
        )}
      </Modal>

      <Modal
        isOpen={modals.addItem}
        onClose={() => closeModal('addItem')}
        title={`Add Item to: ${currentInventory?.inventoryName || ''}`}
        size="lg"
      >
        <AddItemForm
          item={newItem}
          onChange={setNewItem}
          errors={formErrors}
          onSubmit={handleAddItem}
          onCancel={() => closeModal('addItem')}
          isLoading={isLoading}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteInventory}
        title="Delete Inventory"
        itemName={deleteModal.inventory?.inventoryName || ''}
        itemType="inventory"
        isLoading={deleteModal.isLoading}
        itemDetails={deleteModal.inventory ? {
          description: deleteModal.inventory.description,
          itemCount: deleteModal.inventory.itemCount,
          totalWeight: deleteModal.inventory.totalWeight,
          createdAt: deleteModal.inventory.createdAt
        } : null}
        warningMessage={
          deleteModal.inventory?.itemCount > 0
            ? `This inventory contains ${deleteModal.inventory.itemCount} items that will also be deleted.`
            : null
        }
      />

      <DeleteItemConfirmationModal
        isOpen={deleteItemModal.isOpen}
        onClose={() => !deleteItemModal.isLoading && setDeleteItemModal({ isOpen: false, item: null, isLoading: false })}
        onConfirm={confirmDeleteItem}
        itemName={deleteItemModal.item?.materialName || ''}
        isLoading={deleteItemModal.isLoading}
      />
    </div>
  );
};

export default InventoryOverview;